/** Renders one field from a catalog item's `form_schema`.
 *
 * Lifted out of the portal's request form so the catalog item page, the request
 * form and the admin form preview all draw a schema the same way. Three
 * renderers for one schema would drift, and the one that drifted would be the
 * preview — which is the one a service owner trusts when they design the form.
 *
 * The set of types here is not a design choice. `tickets/form_schema.py`
 * validates a submission against the same schema server-side, so a control this
 * file can draw but the server rejects would let somebody fill in a form and
 * lose it on submit. Adding a type means changing both, together.
 */
import { Checkbox, Input, Label, Select, Textarea } from './primitives'
import { cn } from '../../lib/utils'

export interface SchemaField {
  name: string
  label?: string
  type?: string
  required?: boolean
  options?: string[]
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null
  return (
    <p id={id} className="mt-1 text-xs text-critical">
      {message}
    </p>
  )
}

export function SchemaFieldInput({
  field,
  value,
  error,
  onChange,
  disabled = false,
}: {
  field: SchemaField
  value: string
  error?: string
  onChange: (value: string) => void
  /** Set on the admin preview, where the form is being looked at rather than
   *  filled in. */
  disabled?: boolean
}) {
  const label = field.label ?? field.name
  const id = `field-${field.name}`
  const errorId = `${id}-error`
  const invalid = Boolean(error)
  const shared = {
    id,
    disabled,
    'aria-invalid': invalid || undefined,
    'aria-describedby': invalid ? errorId : undefined,
    className: cn('mt-1', invalid && 'border-critical'),
  }

  return (
    <div>
      <Label htmlFor={id}>
        {label}
        {field.required && <span className="ml-0.5 text-critical">*</span>}
      </Label>

      {field.type === 'textarea' ? (
        <Textarea {...shared} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === 'select' ? (
        <Select {...shared} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose…</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      ) : field.type === 'checkbox' ? (
        <div className="mt-1.5">
          <Checkbox
            id={id}
            disabled={disabled}
            checked={value === 'true'}
            onChange={(e) => onChange(e.target.checked ? 'true' : '')}
          />
        </div>
      ) : (
        <Input
          {...shared}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      <FieldError id={errorId} message={error} />
    </div>
  )
}
